import React from "react";
import {
  ConfigurationFormType,
  ConfigurationPageTable,
  ConfigurationPageType,
  ConfigurationTableFilterType,
} from "../interfaces";
import { FormatType } from "../../Alert/Filter/FilterType";
import { InputAdornment } from "@mui/material";
import { replaceNonNumeric } from "../../services/utils";
import { AutopilotMinStayWarning } from "../modalInputs/AutopilotMinStayWarning";

export const SETUP_AUTOPILOT_PAGE: ConfigurationPageTable = {
  type: ConfigurationPageType.TABLE,
  label: "AUTOPILOT",
  menuLabel: "AUTOPILOT",
  pageLabel: "AUTOPILOT_TITLE_SETUP",
  path: "autopilot",
  hideInMenu: true,
  flagRequired: "is_active_autopilot",
  description: "CONF_DESC_SETUP_AUTOPILOT",
  model: {
    entityName: "autopilot",
    label: "AUTOPILOT",
    path: "/api/autopilot-rules",
    name: "Autopilot",
    attributes: [
      {
        key: "properties[].hebergement_nom_court",
        order: 1,
        label: "PROPERTIES",
        type: FormatType.TEXT_FALLBACK_ALL_COUNT,
        numberOfColumnItemsToDisplay: 2,
        mustTranslateOptions: false,
        visible: true,
      },
      {
        key: "uh_types_nandc[].name",
        order: 2,
        label: "UH_TYPES_NANDC",
        type: FormatType.TEXT_FALLBACK_ALL_COUNT,
        numberOfColumnItemsToDisplay: 2,
        mustTranslateOptions: false,
        visible: true,
      },
      {
        key: "actions[].code",
        order: 3,
        label: "ACTION_RULES",
        type: FormatType.TEXT_FALLBACK_ALL_COUNT,
        numberOfColumnItemsToDisplay: 1,
        mustTranslateOptions: true,
        visible: true,
      },
      {
        align: "right",
        key: "application_start_date",
        order: 4,
        label: "APPLICATION_START_DATE",
        type: FormatType.DATE,
        format: {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        },
        visible: true,
      },
      {
        align: "right",
        key: "application_end_date",
        order: 5,
        label: "APPLICATION_END_DATE",
        type: FormatType.DATE,
        format: {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        },
        visible: true,
      },
      {
        key: "run_on_weekdays",
        order: 6,
        label: "DAYS_OF_APPLICATION",
        type: FormatType.WEEKDAYS_ROUNDED,
        visible: true,
      },
      {
        key: "weekdays",
        order: 7,
        label: "DAYS_OF_OCCUPATION",
        type: FormatType.WEEKDAYS_ROUNDED,
        visible: true,
      },
      {
        align: "right",
        key: "occupation_start_date",
        order: 8,
        label: "OCCUPATION_START_DATE",
        type: FormatType.DATE,
        format: {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        },
        visible: true,
      },
      {
        align: "right",
        key: "occupation_end_date",
        order: 9,
        label: "OCCUPATION_END_DATE",
        type: FormatType.DATE,
        format: {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        },
        visible: true,
      },
      {
        key: "lead_time_min",
        order: 10,
        label: "LEAD_TIME_MIN",
        type: FormatType.NUMBER,
        visible: true,
      },
      {
        key: "lead_time_max",
        order: 11,
        label: "LEAD_TIME_MAX",
        type: FormatType.NUMBER,
        visible: true,
      },
      {
        key: "is_enabled",
        order: 12,
        label: "AUTOPILOT_ENABLE",
        type: FormatType.BOOLEAN,
        visible: true,
      },
    ],
    filters: [
      {
        type: ConfigurationTableFilterType.SelectMultiple,
        mustTranslateOptions: false,
        api: "/api/properties/",
        apiParams: {
          page_size: 100,
          ordering: "hebergement_nom_court",
        },
        key: "properties",
        labelKey: "hebergement_nom_court",
        label: "PROPERTIES",
        lookup: "properties",
        controlledInputSize: true,
      },
      {
        type: ConfigurationTableFilterType.SelectMultiple,
        mustTranslateOptions: false,
        api: "/api/uh-types-nandc/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
        },
        key: "uh_types_nandc",
        labelKey: "short_name",
        label: "UHTYPES_NC",
        lookup: "uh_types_nandc",
        controlledInputSize: true,
      },
      {
        type: ConfigurationTableFilterType.SelectMultiple,
        mustTranslateOptions: true,
        api: "/api/autopilot-rule-actions/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
        },
        key: "id",
        labelKey: "code",
        label: "ACTION_RULES",
        lookup: "actions",
        controlledInputSize: true,
      },
      {
        type: ConfigurationTableFilterType.ResetButton,
      },
    ],
    // disable export for now, not yet functionnal
    // exportPath: "/api/autopilot-rules/export",
    actions: [
      {
        type: "link",
        // dim 38 = owner; 20002 = revbell.autopilot technical user (see backend AUTOPILOT_USER_ID)
        href: "/price/?dim=38:20002",
        label: "VIEW_AUTOPILOT_WORKFLOW_UPDATES",
      },
    ],
  },
  options: {
    formFields: [
      {
        key: "is_enabled",
        label: "AUTOPILOT_ENABLE",
        type: ConfigurationFormType.Check,
      },
      {
        type: ConfigurationFormType.SelectValues,
        api: "/api/properties/",
        apiParams: {
          page_size: 100,
          ordering: "hebergement_nom_court",
        },
        key: "properties",
        label: "PROPERTIES",
        labelKey: "hebergement_nom_court",
        watchLookup: "properties",
      },
      {
        type: ConfigurationFormType.SelectValues,
        api: "/api/uh-types-nandc/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
        },
        key: "uh_types_nandc",
        label: "UH_TYPES_NANDC",
        labelKey: "short_name",
      },
      {
        type: ConfigurationFormType.SelectValues,
        api: "/api/autopilot-rule-actions/",
        apiParams: {
          page_size: 100,
          ordering: "label",
        },
        key: "actions",
        label: "ACTION_RULES",
        labelKey: "label",
      },
      {
        key: "minStayWarning",
        type: ConfigurationFormType.Warning,
        label: "AUTOPILOT_MIN_STAY_WARNING_GENERIC",
        component: AutopilotMinStayWarning,
      },
      {
        key: "is_buy_up_grid_type_active",
        label: "AUTOPILOT_CASCADE_BUY_UP_GRID_TYPE",
        type: ConfigurationFormType.Check,
      },
      {
        key: "separatorApplication",
        label: "AUTOPILOT_SECTION_APPLICATION",
        subLabel: "AUTOPILOT_SECTION_APPLICATION_DESC",
        type: ConfigurationFormType.Separator,
      },
      {
        key: "date_range_app",
        startDateField: "application_start_date",
        endDateField: "application_end_date",
        startDateLabel: "APPLICATION_START_DATE_INCLUDED",
        endDateLabel: "APPLICATION_END_DATE_INCLUDED",
        type: ConfigurationFormType.DateRangePicker,
        disablePast: true,
        lockPast: true,
      },
      {
        key: "run_on_weekdays",
        label: "DAYS_OF_APPLICATION",
        type: ConfigurationFormType.WeekDaysRounded,
      },
      {
        key: "separatorOccupation",
        label: "AUTOPILOT_SECTION_OCCUPATION",
        subLabel: "AUTOPILOT_SECTION_OCCUPATION_DESC",
        type: ConfigurationFormType.Separator,
      },
      {
        key: "date_range_occ",
        startDateField: "occupation_start_date",
        endDateField: "occupation_end_date",
        startDateLabel: "OCCUPATION_START_DATE_INCLUDED",
        endDateLabel: "OCCUPATION_END_DATE_INCLUDED",
        type: ConfigurationFormType.DateRangePicker,
        disablePast: true,
        lockPast: true,
      },
      {
        key: "weekdays",
        label: "DAYS_OF_OCCUPATION",
        type: ConfigurationFormType.WeekDaysRounded,
      },
      {
        key: "separatorLeadTime",
        label: "AUTOPILOT_SECTION_LEAD_TIME",
        subLabel: "AUTOPILOT_SECTION_LEAD_TIME_DESC",
        type: ConfigurationFormType.Separator,
      },
      {
        key: "lead_time_min",
        label: "LEAD_TIME_MIN",
        type: ConfigurationFormType.Input,
        inputReplacer: (value = "") => replaceNonNumeric(value),
        inputProps: {
          inputMode: "numeric",
          startAdornment: <InputAdornment position="start">-</InputAdornment>,
        },
        defaultValue: "0",
      },
      {
        key: "lead_time_max",
        label: "LEAD_TIME_MAX",
        type: ConfigurationFormType.Input,
        inputReplacer: (value = "") => replaceNonNumeric(value),
        inputProps: {
          inputMode: "numeric",
          startAdornment: <InputAdornment position="start">-</InputAdornment>,
        },
        defaultValue: "365",
      },
    ],
    creatable: true,
    createOptions: [
      {
        key: "is_enabled",
        flex: "100%",
        required: true,
      },
      {
        key: "properties",
        flex: "100%",
        required: false,
      },
      {
        key: "uh_types_nandc",
        flex: "100%",
        required: false,
      },
      {
        key: "actions",
        flex: "100%",
        required: true,
      },
      {
        key: "minStayWarning",
        flex: "100%",
      },
      {
        key: "is_buy_up_grid_type_active",
        flex: "100%",
        required: false,
      },
      {
        key: "separatorApplication",
        flex: "100%",
      },
      {
        key: "date_range_app",
        flex: "100%",
        required: false,
      },
      {
        key: "run_on_weekdays",
        flex: "100%",
        required: true,
      },
      {
        key: "separatorOccupation",
        flex: "100%",
      },
      {
        key: "date_range_occ",
        flex: "100%",
        required: false,
      },
      {
        key: "weekdays",
        flex: "100%",
        required: true,
      },
      {
        key: "separatorLeadTime",
        flex: "100%",
      },
      {
        key: "lead_time_min",
        flex: "50%",
        required: true,
      },
      {
        key: "lead_time_max",
        flex: "50%",
        required: true,
      },
    ],
    copyable: false,
    editable: true,
    editOptions: [
      {
        key: "is_enabled",
        flex: "100%",
        required: true,
      },
      {
        key: "properties",
        flex: "100%",
        required: false,
      },
      {
        key: "uh_types_nandc",
        flex: "100%",
        required: false,
      },
      {
        key: "actions",
        flex: "100%",
        required: true,
      },
      {
        key: "minStayWarning",
        flex: "100%",
      },
      {
        key: "is_buy_up_grid_type_active",
        flex: "100%",
        required: false,
      },
      {
        key: "separatorApplication",
        flex: "100%",
      },
      {
        key: "date_range_app",
        flex: "100%",
        required: false,
      },
      {
        key: "run_on_weekdays",
        flex: "100%",
        required: true,
      },
      {
        key: "separatorOccupation",
        flex: "100%",
      },
      {
        key: "date_range_occ",
        flex: "100%",
        required: false,
      },
      {
        key: "weekdays",
        flex: "100%",
        required: true,
      },
      {
        key: "separatorLeadTime",
        flex: "100%",
      },
      {
        key: "lead_time_min",
        flex: "50%",
        required: true,
      },
      {
        key: "lead_time_max",
        flex: "50%",
        required: true,
      },
    ],
    deletable: true,
  },
};
